import * as React from 'react';
import { Trans, withTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { differenceInWeeks, parseISO } from 'date-fns';
import { connect } from 'react-redux';
import { ThemeConsumer } from '../theme';
import { Menu, MenuButton, MenuList, MenuItem } from '../menu';
import { Box } from '../box';
import { Icon } from '../icon';
import { Button } from '../button';
import { Text } from '../text';
import { can } from '../../utils/permissions';
import { getPriceProperties, getSponsorValue } from '../../utils/programs';

import { Card } from 'ui';
import variables from 'common/styles/variables.json';
import { CreateRequestToJoin } from 'features/programs/request-to-join';
import { ContactUsModal } from 'features/programs/contact-us';

export class Renderer extends React.Component {
  state = {
    isCreateRequestToJoinOpened: false,
    isContactUsModalOpened: false,
  };

  actions = [
    {
      action: {
        id: 'edit',
        icon: 'pen',
        label: <Trans>Edit</Trans>,
        handler: this.props.onEditCourse,
      },
      visible: (props) => can(props.course, 'Course', 'editProgram'),
    },
    {
      action: {
        id: 'finish',
        icon: 'check',
        label: <Trans>Archive</Trans>,
        handler: this.props.onFinishCourse,
      },
      visible: (props) => can(props.course, 'Course', 'finish'),
    },
    {
      action: {
        id: 'publish',
        icon: 'check',
        label: <Trans>Publish</Trans>,
        handler: this.props.onPublishCourse,
      },
      disabledInfo: 'Please adjust program start time into future',
      visible: (props) => can(props.course, 'Course', 'publishable'),
      disabled: (props) => !can(props.course, 'Course', 'publish'),
    },
    {
      action: {
        id: 'duplicate',
        icon: 'plus',
        label: <Trans>Duplicate</Trans>,
        handler: this.props.onDuplicateCourse,
      },
      visible: (props) => can(props.course, 'Course', 'duplicate'),
    },
    {
      action: {
        id: 'delete',
        icon: 'delete',
        label: <Trans>Delete</Trans>,
        handler: this.props.onDeleteCourse,
      },
      visible: (props) => can(props.course, 'Course', 'delete'),
    },
    {
      action: {
        id: 'report',
        icon: 'notification--message',
        label: <Trans>Report</Trans>,
        handler: this.props.onReportCourse,
      },
    },
  ];

  async handleCourseJoinClick(course) {
    if (can(course, 'Course', 'isMemberOfProgram')) {
      await this.props.decline(course.id);
      this.props.onHandleJoin?.();
    } else if (can(course, 'Course', 'canUserRequestContactUs')) {
      this.setState({ isContactUsModalOpened: true });
    } else if (!can(course, 'Course', 'hasUserSentJoinRequest') && course.privacy === 'private') {
      this.setState({ isCreateRequestToJoinOpened: true });
    } else {
      await this.props.accept(course.id);
      this.props.onHandleJoin?.();
    }
  }

  handleCourseActionButtonTitle = (course) => {
    if (can(course, 'Course', 'hasUserSentContactUsRequest')) {
      return <Trans>Contact request sent</Trans>;
    }

    if (can(course, 'Course', 'canUserRequestContactUs')) {
      return <Trans>Contact us</Trans>;
    }

    if (can(course, 'Course', 'joinedAsUser')) {
      return <Trans>Unsubscribe</Trans>;
    }

    return (
      <Trans>
        {course.privacy === 'private'
          ? can(course, 'Course', 'hasUserSentJoinRequest')
            ? 'Request Sent'
            : 'Request to Subscribe'
          : 'Subscribe'}
      </Trans>
    );
  };

  renderDropdown() {
    return (
      <Menu>
        <MenuButton className="button-dropdown">
          <Icon name="menu" size={20} color="#CFD8DC" />
        </MenuButton>
        <MenuList style={{ width: '300px' }}>
          {this.actions
            .filter((action) => (action.visible ? action.visible(this.props) : true))
            .map((data) => (
              <MenuItem
                key={data.action.id}
                disabled={data.disabled?.(this.props)}
                disabledInfo={data.disabledInfo}
                onSelect={data.action.handler}
              >
                {data.action.label}
                <Icon size={15} name={data.action.icon} color="#C4C4C4" />
              </MenuItem>
            ))}
        </MenuList>
      </Menu>
    );
  }

  renderPrice(course, color) {
    const { totalPrice, paidBySponsors, missingToPay, isContactUs, isFree } = getPriceProperties(course);
    return (
      <div style={{ display: 'inline-block', margin: '5px 0' }}>
        {isFree ? (
          <Text size={14} color={color.brand}>
            <Trans>Free Program</Trans>
          </Text>
        ) : isContactUs ? (
          <Text size={14} color={color.brand}>
            <Trans>Contact Us</Trans>
          </Text>
        ) : paidBySponsors > 0 ? (
          <div style={{ display: 'inline-block' }}>
            <Text size={14} color="#717E94" style={{ textDecoration: 'line-through', marginRight: 5 }}>
              ${totalPrice.toFixed(2)}
            </Text>
            <Text size={14} color={color.brand}>
              ${missingToPay.toFixed(2)}
            </Text>
          </div>
        ) : totalPrice ? (
          <Text size={14} color={color.brand}>
            ${totalPrice.toFixed(2)}
          </Text>
        ) : null}
      </div>
    );
  }

  render() {
    const { course } = this.props;
    const weeksCount = differenceInWeeks(parseISO(course.end_time), parseISO(course.start_time));
    const linkToProgram = `/courses/${course.id}/${can(course, 'Course', 'isMemberOfProgram') ? 'timeline' : 'about'}`;

    return (
      <>
        <ThemeConsumer>
          {(theme) => (
            <Card shrinked>
              <div className="program-card">
                {this.renderDropdown()}
                <Link to={linkToProgram}>
                  <div className="program-card__image">
                    <img src={course.cover_photo.url} alt="Program cover" />
                    <div className="program-card__image-inner">
                      <div className="program-card__image-inner__text">{course.name.substring(0, 80)}</div>
                      {course.author_name ? (
                        <div className="program-card__image-inner__subtext">
                          {this.props.t('authorWeeks', { name: course.author_name, weeks: weeksCount })}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </Link>
                <div className="program-card__body">
                  <Box flexDirection="row" justifyContent="space-between" alignItems="center">
                    <div className="program-card__body__upper">
                      <Text size={14} color={theme.color.brand}>
                        {this.props.t('programDurationDates', {
                          startDate: new Date(course.start_time),
                          endDate: new Date(course.end_time),
                        })}
                      </Text>
                      <div style={{ maxHeight: 53, overflow: 'hidden' }}>
                        {this.renderPrice(course, theme.color.brand)}
                        {course.sponsors?.length
                          ? course.sponsors.map((sponsor) => (
                              <Text
                                key={sponsor.name}
                                size={12}
                                color="#717E94"
                                component="div"
                                style={{ marginTop: 6 }}
                              >
                                {getSponsorValue(course.price, sponsor.price)} <Trans>Sponsored by</Trans>{' '}
                                {sponsor.name}
                              </Text>
                            ))
                          : null}
                      </div>
                    </div>
                  </Box>

                  {this.props.myPrograms ? (
                    <div className="program-card__body__footer">
                      <Button
                        textColor={theme.color.brand}
                        onClick={this.props.onRequestCourse}
                        size="md"
                        title={<Trans>Enter program</Trans>}
                        className="program-card__body__join-button program-card__body__join-button--uppercase"
                      />
                    </div>
                  ) : (
                    <>
                      <Box
                        flexDirection="row"
                        justifyContent="flex-start"
                        alignItems="center"
                        className="program-card__body__learn-more-wrapper"
                      >
                        <div>
                          <Link to={linkToProgram} className="program-card__body__learn-more">
                            <Trans>Learn more</Trans>
                          </Link>
                          {course.members_count} <Trans>registered</Trans>
                        </div>
                      </Box>

                      {course.status === 'published' && can(course, 'Course', 'rateProgramObject') && (
                        <div className="program-card__body__footer">
                          <Button
                            textColor={
                              can(course, 'Course', 'joinedAsUser') ? theme.color.red : variables.brandColor.value
                            }
                            onClick={() => this.handleCourseJoinClick(course)}
                            size="md"
                            title={this.handleCourseActionButtonTitle(course)}
                            className="program-card__body__join-button"
                            disabled={
                              can(course, 'Course', 'hasUserSentJoinRequest') ||
                              can(course, 'Course', 'hasUserSentContactUsRequest')
                            }
                          />
                          <CreateRequestToJoin
                            isOpened={this.state.isCreateRequestToJoinOpened}
                            program={course}
                            onSuccess={() => this.setState({ isCreateRequestToJoinOpened: false })}
                            onCancel={() => this.setState({ isCreateRequestToJoinOpened: false })}
                          />
                          <ContactUsModal
                            isOpened={this.state.isContactUsModalOpened}
                            onClose={() => this.setState({ isContactUsModalOpened: false })}
                            programId={course.id}
                            onSuccess={() => {
                              this.props.reloadProgramData(course.id);
                              this.setState({ isContactUsModalOpened: false });
                            }}
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </Card>
          )}
        </ThemeConsumer>
      </>
    );
  }
}

const mapDispatch = (dispatch) => ({
  accept: (id) => dispatch.courses.accept(id),
  decline: (id) => dispatch.courses.decline(id),
  reloadProgramData: (programId) => dispatch.courses.find(programId),
});

export const CourseCard = withTranslation()(
  connect(() => {
    return {};
  }, mapDispatch)(Renderer),
);
